import React from 'react';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Checkbox } from '@oysterjs/ui/checkbox';
import { DataTable, type ColumnDef } from '@oysterjs/ui/data-table';
import { TableHeader } from '@oysterjs/ui/table';
import { ErrorMessage } from '@oysterjs/ui/fieldset';
import { Spinner } from '@oysterjs/ui/spinner';

import { client } from '~/api/client';
import {
  CustomPresetAccessFieldsFragment,
  GetCustomPresetShareInfoDocument,
  GetCustomPresetShareInfoQuery,
  GetFederatedChildAgenciesDocument,
  GetFederatedChildAgenciesQuery,
  GrantCustomPresetAccessDocument,
  GrantCustomPresetAccessMutation,
  RevokeCustomPresetAccessDocument,
  RevokeCustomPresetAccessMutation
} from '~/api/graphql';
import {
  shareAgencyRows,
  shareErrorMessage,
  shareSelectionDiff,
  shareUpdateFailureMessage,
  type ShareAgencyRow
} from './presets.utils';

interface PresetShareDialogProps {
  open: boolean;
  presetId: string;
  presetTitle: string;
  onClose: () => void;
}

type ShareRow = ShareAgencyRow<{ id: string; name: string }>;

const uniq = <T,>(arr: T[]) => [...new Set(arr)];

// DataTable applies its sticky-header classes only to columns that use `header`;
// a column with a custom `renderHeader` gets them verbatim from us or not at all
// (data-table.tsx renderHeaderCell). Without them this cell stays static and
// transparent, so it scrolls away while the rest of the header stays pinned, and
// the body's checkboxes — positioned, via Checkbox's `relative isolate` inside a
// `relative` TableCell — paint straight over the gap it leaves.
const STICKY_HEADER_CLASS = 'sticky top-0 z-10 bg-white dark:bg-neutral-800';

/**
 * Parent-side share management for a custom preset (EPD-2032). A checkbox per
 * federated child agency represents whether the preset is shared with it, and Update
 * reconciles the checked set against the current grants — granting what was checked
 * and revoking what was unchecked. There is a single role (VIEWER), so no role
 * picker. The backend is the authority on shareability — PUBLIC presets only,
 * federated children only — so its errors are rendered inline.
 */
export const PresetShareDialog = ({
  open,
  presetId,
  presetTitle,
  onClose
}: PresetShareDialogProps) => {
  const [accessList, setAccessList] = React.useState<CustomPresetAccessFieldsFragment[]>([]);
  const [childAgencies, setChildAgencies] = React.useState<{ id: string; name: string }[] | null>(
    null
  );
  const [loading, setLoading] = React.useState(false);
  const [selectedAgencyIds, setSelectedAgencyIds] = React.useState<string[]>([]);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>();

  // Bumped on each load and on its teardown. Async handlers capture it and drop
  // their writes once it moves, because closing this dialog does NOT unmount it —
  // presets.tsx keeps it mounted while a preset is selected. Without the check, an
  // update started for one preset lands its results in the dialog now showing
  // another.
  const loadGeneration = React.useRef(0);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    loadGeneration.current += 1;
    const generation = loadGeneration.current;
    const isCurrent = () => generation === loadGeneration.current;
    setLoading(true);
    setError(undefined);
    setSelectedAgencyIds([]);
    // Clear the in-flight flag too: an update abandoned by this reload bails at its
    // generation check before reaching its own reset, which would otherwise leave
    // Update permanently disabled.
    setIsUpdating(false);
    // Clear loaded data as well, so a failed load cannot leave the previous preset's
    // grants on screen as if they were this one's.
    setAccessList([]);
    setChildAgencies(null);
    Promise.all([
      client.query<GetCustomPresetShareInfoQuery>({
        query: GetCustomPresetShareInfoDocument,
        variables: { id: presetId }
      }),
      client.query<GetFederatedChildAgenciesQuery>({
        query: GetFederatedChildAgenciesDocument
      })
    ])
      .then(([shareInfo, agencies]) => {
        if (!isCurrent()) {
          return;
        }
        const loadedAccess = shareInfo.data?.customPreset?.accessList ?? [];
        setAccessList(loadedAccess);
        // Checked means shared, so the initial selection is what is already granted.
        setSelectedAgencyIds(loadedAccess.map((access) => access.agency.id));
        if (agencies.data?.federatedChildAgencies) {
          setChildAgencies(agencies.data.federatedChildAgencies);
        }
        setError(
          shareErrorMessage(shareInfo.errors, 'Failed to load sharing details.') ??
            shareErrorMessage(agencies.errors, 'Failed to load child agencies.')
        );
      })
      .catch(() => {
        if (isCurrent()) {
          setError('Failed to load sharing details.');
        }
      })
      .finally(() => {
        if (isCurrent()) {
          setLoading(false);
        }
      });
    return () => {
      loadGeneration.current += 1;
    };
  }, [open, presetId]);

  const rows = React.useMemo(
    () => shareAgencyRows(childAgencies, accessList),
    [childAgencies, accessList]
  );

  const grantedAgencyIds = React.useMemo(
    () => accessList.map((access) => access.agency.id),
    [accessList]
  );

  const { toGrant, toRevoke } = React.useMemo(
    () => shareSelectionDiff(grantedAgencyIds, selectedAgencyIds),
    [grantedAgencyIds, selectedAgencyIds]
  );
  const hasChanges = toGrant.length > 0 || toRevoke.length > 0;

  const allSelected =
    !!childAgencies &&
    childAgencies.length > 0 &&
    childAgencies.every((agency) => selectedAgencyIds.includes(agency.id));

  /**
   * Reconciles the checked set with the current grants: one mutation per change, in a
   * deterministic order, so every failure is attributable to one agency. The loop
   * stops early if the dialog moved on (see loadGeneration).
   *
   * The access list is rebuilt from the mutation responses rather than refetched —
   * grantCustomPresetAccess returns the whole new row, and revoke returns only a
   * boolean, so a successful revoke just drops its row. The selection is then reset
   * to whatever is actually granted, which keeps "checked means shared" true even
   * when part of the fan-out failed; the inline error names what did not change.
   */
  const handleUpdate = async () => {
    const generation = loadGeneration.current;
    if (!hasChanges) {
      return;
    }
    setIsUpdating(true);
    setError(undefined);

    const nameById = new Map((childAgencies ?? []).map((agency) => [agency.id, agency.name]));
    const failures: Array<{ name: string; message: string }> = [];
    let nextAccess = [...accessList];

    for (const agencyId of toGrant) {
      if (generation !== loadGeneration.current) {
        return;
      }
      const name = nameById.get(agencyId) ?? agencyId;
      try {
        const res = await client.mutate<GrantCustomPresetAccessMutation>({
          mutation: GrantCustomPresetAccessDocument,
          variables: { customPresetId: presetId, agencyId }
        });
        const message = shareErrorMessage(res.errors, 'Failed to share the preset.');
        if (message) {
          failures.push({ name, message });
        } else if (res.data?.grantCustomPresetAccess) {
          nextAccess = [...nextAccess, res.data.grantCustomPresetAccess];
        }
      } catch {
        failures.push({ name, message: 'Failed to share the preset.' });
      }
    }

    for (const agencyId of toRevoke) {
      if (generation !== loadGeneration.current) {
        return;
      }
      const name = nameById.get(agencyId) ?? agencyId;
      try {
        const res = await client.mutate<RevokeCustomPresetAccessMutation>({
          mutation: RevokeCustomPresetAccessDocument,
          variables: { customPresetId: presetId, agencyId }
        });
        const message = shareErrorMessage(res.errors, 'Failed to revoke access.');
        if (message) {
          failures.push({ name, message });
        } else {
          nextAccess = nextAccess.filter((access) => access.agency.id !== agencyId);
        }
      } catch {
        failures.push({ name, message: 'Failed to revoke access.' });
      }
    }

    if (generation !== loadGeneration.current) {
      return;
    }
    setAccessList(nextAccess);
    setSelectedAgencyIds(nextAccess.map((access) => access.agency.id));
    setError(shareUpdateFailureMessage(failures));
    setIsUpdating(false);
  };

  const columns: ColumnDef<ShareRow, never, never>[] = [
    {
      id: 'select',
      noHref: true,
      cellClassName: 'w-0 py-1.5 px-3',
      renderHeader: () => {
        // With a single agency, select-all would only duplicate that row's checkbox.
        if (!childAgencies || childAgencies.length <= 1) {
          return <TableHeader className={`w-0 py-1.5 px-3 ${STICKY_HEADER_CLASS}`} />;
        }
        return (
          <TableHeader className={`w-0 py-1.5 px-3 text-xs ${STICKY_HEADER_CLASS}`}>
            <Checkbox
              aria-label="Select all agencies"
              color="sky"
              checked={allSelected}
              indeterminate={selectedAgencyIds.length > 0 && !allSelected}
              onChange={(checked) =>
                setSelectedAgencyIds(checked ? childAgencies.map((agency) => agency.id) : [])
              }
            />
          </TableHeader>
        );
      },
      renderCell: (row) => (
        <Checkbox
          aria-label={row.agency.name}
          color="sky"
          checked={selectedAgencyIds.includes(row.agency.id)}
          onChange={(checked) =>
            setSelectedAgencyIds((prev) =>
              checked ? uniq([...prev, row.agency.id]) : prev.filter((id) => id !== row.agency.id)
            )
          }
        />
      )
    },
    {
      id: 'agency',
      header: 'Agency',
      renderCell: (row) => (
        <span className="flex min-w-0 items-baseline gap-2">
          <span
            className="truncate text-sm font-medium text-neutral-900 dark:text-white"
            title={row.agency.name}
          >
            {row.agency.name}
          </span>
          {row.access && (
            <span className="flex-none text-xs text-neutral-500 dark:text-neutral-400">
              Shared{' '}
              {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                new Date(row.access.createdAt)
              )}
            </span>
          )}
        </span>
      )
    }
  ];

  return (
    <Dialog open={open} onClose={onClose} size="3xl">
      <DialogTitle>Share preset</DialogTitle>
      <DialogDescription>
        Agencies you share <span className="font-semibold">{presetTitle}</span> with can run it, but
        not edit it.
      </DialogDescription>

      <DialogBody>
        {/* A null child list after loading means the initial load failed; only the
            inline error below is shown in that case. */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner size="size-6" color="neutral" />
          </div>
        ) : childAgencies === null ? null : childAgencies.length === 0 ? (
          <div className="rounded-lg bg-neutral-50 p-8 text-center dark:bg-neutral-800">
            <UserGroupIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
            <p className="mt-4 text-sm font-medium text-neutral-900 dark:text-white">
              No child agencies available
            </p>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              You don't have any child agencies with federated billing to share with.
            </p>
          </div>
        ) : (
          /* Fixed height, not max-height: the dialog stays the same size whether the
             agency has two children or fifty. This wrapper is the scroll container —
             vertical only, since a sideways scrollbar on two narrow columns is never
             the right answer — which is also what the sticky header pins against.
             DataTable's own `scrollable` is deliberately not used: it applies
             overflow-auto, which scrolls both axes. */
          <div className="h-96 overflow-y-auto overflow-x-hidden px-4 sm:px-6 rounded-lg border border-neutral-200 dark:border-neutral-700">
            <DataTable<ShareRow, never, never>
              data={rows}
              columns={columns}
              keyExtractor={(row) => row.agency.id}
              tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
              stickyHeader
            />
          </div>
        )}

        {/* role="alert" so a screen reader announces the failure: this sits below the
            table and can be scrolled out of view, where a silent message reads as
            the button having done nothing. */}
        {error && (
          <ErrorMessage role="alert" className="mt-4">
            {error}
          </ErrorMessage>
        )}
      </DialogBody>

      <DialogActions>
        <Button color="white" onClick={onClose}>
          Close
        </Button>
        <Button
          color="sky"
          onClick={handleUpdate}
          disabled={!hasChanges || isUpdating}
          loading={isUpdating}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};
